/* eslint-disable react/prop-types, no-unused-vars */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Spin } from 'antd';
import { showSuccess, showError } from 'common/ToastNotifications';
import { EyeOutlined, SaveOutlined, CloseOutlined, SettingOutlined, PlayCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import PipelineSidebar from './PipelineSidebar';
import TransformConfigurator from './TransformConfigurator';
import PreviewModal from './PreviewModal';
import SavePipelineDialog from './SavePipelineDialog';
import { TRANSFORM_TYPES } from './transformTypes';
import { deriveColumnsAtStep } from './deriveColumns';
import { PipelineRunErrorMessage } from './pipelineRunError';

const PipelineBuilder = ({
    visible,
    onClose,
    datasetId,
    datasetName,
    schemaId,
    businessAreaId,
    columns,
    schemaFields,
    // Pipeline props
    pipelines,
    onCreatePipeline,
    onUpdatePipeline,
    onDeletePipeline,
    onGetPipelines,
    onDryRunPipelineInline,
    onRunPipeline,
    createPipelineResult,
    updatePipelineResult,
    deletePipelineResult,
    runPipelineResult,
    dryRunResult,
    dryRunLoading,
    onGoToJobs,
    initialPipelineId,
    onInitialPipelineLoaded,
}) => {
    const [steps, setSteps] = useState([]);
    const [selectedStepIndex, setSelectedStepIndex] = useState(null);
    const [showCatalog, setShowCatalog] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showSavePipelineDialog, setShowSavePipelineDialog] = useState(false);
    const [currentPipelineId, setCurrentPipelineId] = useState(null);
    const [currentPipelineVersion, setCurrentPipelineVersion] = useState(null);
    const [currentPipelineName, setCurrentPipelineName] = useState('');
    const [saving, setSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [initialLoadFailed, setInitialLoadFailed] = useState(false);

    // Track initial mount values so we don't react to stale Redux state
    const prevCreateResult = useRef(createPipelineResult);
    const prevUpdateResult = useRef(updatePipelineResult);
    const prevDeleteResult = useRef(deletePipelineResult);
    const prevRunResult = useRef(runPipelineResult);
    const prevPipelines = useRef(pipelines);
    const [pipelinesFetched, setPipelinesFetched] = useState(false);

    useEffect(() => {
        if (visible && datasetId) {
            setPipelinesFetched(false);
            onGetPipelines(datasetId);
        }
    }, [visible, datasetId]);

    // Track when pipelines are freshly fetched (not stale cached data)
    useEffect(() => {
        if (visible && pipelines !== prevPipelines.current) {
            prevPipelines.current = pipelines;
            setPipelinesFetched(true);
        }
    }, [visible, pipelines]);

    // Auto-load pipeline if initialPipelineId is provided - only after fresh fetch
    useEffect(() => {
        if (visible && initialPipelineId && pipelinesFetched && pipelines?.Items?.length > 0 && !currentPipelineId && !initialLoadFailed) {
            const pipeline = pipelines.Items.find(p => p.pipelineId === initialPipelineId);
            if (pipeline) {
                setSteps((pipeline.steps || []).map(step => {
                    const typeInfo = TRANSFORM_TYPES.find(t => t.type === step.type);
                    return { ...step, label: typeInfo ? typeInfo.label : step.type };
                }));
                setCurrentPipelineId(pipeline.pipelineId);
                setCurrentPipelineVersion(pipeline.version);
                setCurrentPipelineName(pipeline.pipelineName || '');
                setSelectedStepIndex(null);
                setHasUnsavedChanges(false);
                if (onInitialPipelineLoaded) {
                    onInitialPipelineLoaded();
                }
            } else {
                // Pipeline not found - stop loading and show empty builder
                setInitialLoadFailed(true);
                showError('Pipeline not found. It may have been deleted.');
                if (onInitialPipelineLoaded) {
                    onInitialPipelineLoaded();
                }
            }
        }
    }, [visible, initialPipelineId, pipelinesFetched, pipelines, currentPipelineId, initialLoadFailed, onInitialPipelineLoaded]);

    // Pipeline result watchers — only react when the value actually changes from what we last saw
    useEffect(() => {
        if (createPipelineResult === prevCreateResult.current) return;
        prevCreateResult.current = createPipelineResult;
        if (createPipelineResult?.success && !currentPipelineId) {
            setSaving(false);
            setShowSavePipelineDialog(false);
            setHasUnsavedChanges(false);
            if (createPipelineResult.pipeline?.pipelineId) {
                setCurrentPipelineId(createPipelineResult.pipeline.pipelineId);
                setCurrentPipelineVersion(createPipelineResult.pipeline.version);
            }
            showSuccess('Pipeline saved');
            if (datasetId) onGetPipelines(datasetId);
        } else if (createPipelineResult?.success === false) {
            setSaving(false);
            showError('Failed to save pipeline. You may not have permission to use this feature.');
        }
    }, [createPipelineResult]);

    useEffect(() => {
        if (updatePipelineResult === prevUpdateResult.current) return;
        prevUpdateResult.current = updatePipelineResult;
        if (updatePipelineResult?.success && updatePipelineResult?.pipeline) {
            setSaving(false);
            setShowSavePipelineDialog(false);
            setHasUnsavedChanges(false);
            setCurrentPipelineVersion(updatePipelineResult.pipeline.version);
            showSuccess('Pipeline updated');
            if (datasetId) onGetPipelines(datasetId);
        } else if (updatePipelineResult?.success === false) {
            setSaving(false);
            showError('Save failed — pipeline was modified. Please try again.');
        }
    }, [updatePipelineResult]);

    useEffect(() => {
        if (deletePipelineResult === prevDeleteResult.current) return;
        prevDeleteResult.current = deletePipelineResult;
        if (deletePipelineResult?.success) {
            showSuccess('Pipeline deleted');
            if (datasetId) onGetPipelines(datasetId);
        } else if (deletePipelineResult?.success === false) {
            showError('Failed to delete pipeline');
        }
    }, [deletePipelineResult]);

    useEffect(() => {
        if (runPipelineResult === prevRunResult.current) return;
        prevRunResult.current = runPipelineResult;
        if (runPipelineResult?.success) {
            setGenerating(false);
            showSuccess(<span>Pipeline execution submitted. <a onClick={() => onGoToJobs && onGoToJobs()} style={{ color: '#fff', textDecoration: 'underline' }}>View Jobs</a></span>);
        } else if (runPipelineResult?.success === false) {
            setGenerating(false);
            showError(<PipelineRunErrorMessage error={runPipelineResult.error} />, undefined, { autoClose: 10000 });
        }
    }, [runPipelineResult, onGoToJobs]);

    const addStep = useCallback((type) => {
        const typeInfo = TRANSFORM_TYPES.find(t => t.type === type);
        const newStep = {
            order: steps.length + 1,
            type,
            enabled: true,
            config: {},
            label: typeInfo ? typeInfo.label : type,
        };
        setSteps(prev => [...prev, newStep]);
        setSelectedStepIndex(steps.length);
        setShowCatalog(false);
        setHasUnsavedChanges(true);
    }, [steps.length]);

    const updateStepConfig = useCallback((index, config) => {
        setSteps(prev => prev.map((step, i) => i === index ? { ...step, config } : step));
        setHasUnsavedChanges(true);
    }, []);

    const removeStep = useCallback((index) => {
        setSteps(prev => {
            let newSteps = prev.filter((_, i) => i !== index)
                .map((step, i) => ({ ...step, order: i + 1 }));
            return newSteps;
        });
        if (selectedStepIndex === index) {
            setSelectedStepIndex(null);
        } else if (selectedStepIndex > index) {
            setSelectedStepIndex(prev => prev - 1);
        }
        setHasUnsavedChanges(true);
    }, [selectedStepIndex]);

    const toggleStepEnabled = useCallback((index) => {
        setSteps(prev => prev.map((step, i) =>
            i === index ? { ...step, enabled: !step.enabled } : step
        ));
        setHasUnsavedChanges(true);
    }, []);

    const loadPipeline = useCallback((pipeline) => {
        setSteps((pipeline.steps || []).map(step => {
            const typeInfo = TRANSFORM_TYPES.find(t => t.type === step.type);
            return { ...step, label: typeInfo ? typeInfo.label : step.type };
        }));
        setCurrentPipelineId(pipeline.pipelineId);
        setCurrentPipelineVersion(pipeline.version);
        setCurrentPipelineName(pipeline.pipelineName || '');
        setSelectedStepIndex(null);
        setHasUnsavedChanges(false);
    }, []);

    // Save Pipeline (ETL definition)
    const handleSavePipeline = useCallback((name, description) => {
        setSaving(true);
        setCurrentPipelineName(name);
        const data = {
            pipelineName: name,
            description,
            datasetId,
            steps: steps.map(({ label, ...step }) => step),
        };

        if (currentPipelineId) {
            onUpdatePipeline(currentPipelineId, {
                ...data,
                version: currentPipelineVersion,
            });
        } else {
            onCreatePipeline(data);
        }
    }, [steps, datasetId, currentPipelineId, currentPipelineVersion, onCreatePipeline, onUpdatePipeline]);

    const handleSavePipelineAsNew = useCallback((name, description) => {
        setSaving(true);
        setCurrentPipelineName(name);
        setCurrentPipelineId(null);
        setCurrentPipelineVersion(null);
        const data = {
            pipelineName: name,
            description,
            datasetId,
            steps: steps.map(({ label, ...step }) => step),
        };
        onCreatePipeline(data);
    }, [steps, datasetId, onCreatePipeline]);

    const handlePreview = useCallback(() => {
        onDryRunPipelineInline(datasetId, steps.map(({ label, ...step }) => step));
        setShowPreview(true);
    }, [datasetId, steps, onDryRunPipelineInline]);

    const handleGenerate = useCallback(() => {
        Modal.confirm({
            title: 'Execute Pipeline',
            icon: <ExclamationCircleOutlined />,
            content: `This will apply all enabled transformations in "${currentPipelineName}" to generate a materialized view from the dataset. The underlying dataset will not be modified.`,
            okText: 'Execute',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk() {
                setGenerating(true);
                onRunPipeline(currentPipelineId);
            },
        });
    }, [currentPipelineId, currentPipelineName, onRunPipeline]);

    const handleClose = useCallback(() => {
        setSteps([]);
        setSelectedStepIndex(null);
        setShowCatalog(false);
        setCurrentPipelineId(null);
        setCurrentPipelineVersion(null);
        setCurrentPipelineName('');
        setHasUnsavedChanges(false);
        setGenerating(false);
        setInitialLoadFailed(false);
        setPipelinesFetched(false);
        onClose();
    }, [onClose]);

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            footer={null}
            width="100vw"
            style={{ top: 0, padding: 0, maxWidth: '100vw' }}
            styles={{ body: { height: 'calc(100vh - 55px)', padding: 0, overflow: 'hidden' } }}
            closable={false}
            destroyOnHidden
        >
            <div className="flex h-full flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3" data-testid="pipeline-header" style={{ backgroundColor: '#000000' }}>
                    <div>
                        <h2 className="m-0 text-lg font-semibold text-white">Bulk Data Transformation Pipeline</h2>
                        <p className="m-0 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Dataset: {datasetName}{currentPipelineName ? ` — Pipeline: ${currentPipelineName}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={handlePreview}
                            disabled={steps.length === 0}
                            data-testid="pipeline-preview-button"
                        >
                            Preview
                        </Button>
                        <Button
                            icon={<SaveOutlined />}
                            onClick={() => setShowSavePipelineDialog(true)}
                            disabled={steps.length === 0}
                            data-testid="pipeline-save-pipeline-button"
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                        >
                            Save Pipeline
                        </Button>
                        {currentPipelineId && !hasUnsavedChanges && (
                            <Button
                                icon={<PlayCircleOutlined />}
                                onClick={handleGenerate}
                                disabled={steps.filter(s => s.enabled !== false).length === 0 || generating}
                                loading={generating}
                                data-testid="pipeline-execute-button"
                                style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}
                            >
                                Execute Pipeline
                            </Button>
                        )}
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                            data-testid="pipeline-close-button"
                            style={{ color: '#fff' }}
                        />
                    </div>
                </div>

                {/* Body */}
                {initialPipelineId && !currentPipelineId && !initialLoadFailed ? (
                    <div className="flex flex-1 items-center justify-center bg-gray-50">
                        <Spin size="large" tip="Loading pipeline..." />
                    </div>
                ) : (
                    <div className="flex flex-1 overflow-hidden">
                        {/* Left Panel */}
                        <PipelineSidebar
                            steps={steps}
                            selectedStepIndex={selectedStepIndex}
                            showCatalog={showCatalog}
                            savedPipelines={pipelines?.Items || []}
                            onAddClick={() => setShowCatalog(true)}
                            onSelectStep={setSelectedStepIndex}
                            onRemoveStep={removeStep}
                            onToggleStep={toggleStepEnabled}
                            onAddTransform={addStep}
                            onLoadPipeline={loadPipeline}
                            onDeletePipeline={onDeletePipeline}
                            onCloseCatalog={() => setShowCatalog(false)}
                        />

                        {/* Right Panel */}
                        <div className="flex-1 overflow-auto bg-white p-6">
                            {selectedStepIndex !== null && steps[selectedStepIndex] ? (
                                <TransformConfigurator
                                    step={steps[selectedStepIndex]}
                                    columns={deriveColumnsAtStep(columns, steps, selectedStepIndex)}
                                    onConfigChange={(config) => updateStepConfig(selectedStepIndex, config)}
                                />
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center text-gray-400">
                                    <SettingOutlined className="mb-4 text-6xl" />
                                    <h3 className="text-lg font-medium text-gray-500">No Transformation Selected</h3>
                                    <p className="text-base">Add a transformation from the left panel or select an existing one to configure its settings.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <PreviewModal
                visible={showPreview}
                onClose={() => setShowPreview(false)}
                steps={steps}
                dryRunResult={dryRunResult}
                loading={dryRunLoading}
            />

            <SavePipelineDialog
                visible={showSavePipelineDialog}
                onCancel={() => setShowSavePipelineDialog(false)}
                onSave={handleSavePipeline}
                onSaveAsNew={handleSavePipelineAsNew}
                stepCount={steps.length}
                initialName={currentPipelineName}
                isExistingPipeline={!!currentPipelineId}
                saving={saving}
                title="Pipeline"
            />

        </Modal>
    );
};

export default PipelineBuilder;
